import { useCallback, useState } from "react"

import { graphql } from "@/souhl_admin/gql/__generated__"
import { useSouhlAdminMutation } from "@/provider/apollo/Apollo"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import {
	formatYearMonth,
	type YearMonthValue,
} from "@/company_admin/utils/yearMonth"
import buttonStyles from "./CreateSettlementButton.module.scss"
import dialogStyles from "../ConfirmDialog.module.scss"

const CreateSettlement = graphql(/* GraphQL */ `
	mutation CreateSettlement($input: CreateSettlementInput!) {
		createSettlement(input: $input) {
			created
		}
	}
`)

type Props = {
	accountingGroupId: string
	lastMonth: YearMonthValue
	onCreateComplete: () => void
}

export const CreateSettlementButton = ({
	accountingGroupId,
	lastMonth,
	onCreateComplete,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const [createSettlement, { loading }] = useSouhlAdminMutation(
		CreateSettlement,
		{ context: { disableErrorDialog: true } }
	)

	const handleConfirm = useCallback(async () => {
		setError(null)
		try {
			await createSettlement({
				variables: {
					input: {
						accountingGroupId,
						yearMonth: formatYearMonth(lastMonth),
					},
				},
			})
			setIsOpen(false)
			onCreateComplete()
		} catch (e) {
			setError(e instanceof Error ? e.message : "エラーが発生しました。")
		}
	}, [createSettlement, accountingGroupId, lastMonth, onCreateComplete])

	return (
		<>
			<button
				type="button"
				className={buttonStyles.create_button}
				onClick={() => {
					setError(null)
					setIsOpen(true)
				}}
			>
				{lastMonth.month}月成果分相殺明細を作成する
			</button>

			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={dialogStyles.dialog_content}>
					<h2 className={dialogStyles.title}>相殺明細の作成</h2>
					{error && <p className={dialogStyles.error}>{error}</p>}
					<p className={dialogStyles.message}>
						{lastMonth.year}年{lastMonth.month}
						月成果分の相殺明細を作成します。よろしいですか？
					</p>
					<div className={dialogStyles.button_container}>
						<button
							type="button"
							className={dialogStyles.cancel_button}
							onClick={() => setIsOpen(false)}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={dialogStyles.confirm_button}
							onClick={handleConfirm}
							disabled={loading}
						>
							{loading ? "作成中..." : "作成する"}
						</button>
					</div>
				</div>
			</ModalDialog>
		</>
	)
}
